'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { VStack, Box, HStack } from '@terra/ui-styled-system';
import { MarkdownPreview } from '../markdown-preview';
import { EmailInboxCard } from '../artifacts/email/EmailInboxArtifact';
import { parseEmailInboxPayload } from '../artifacts/email/email-inbox-payload';
import {
  OobEvidenceLabel,
  OobInteractionsCard,
} from '../artifacts/oob/OobInteractionsCard';
import { parseOobEvidencePayload } from '../artifacts/oob/oob-evidence-payload';
import { WebSocketTranscriptCard } from '../artifacts/websocket/WebSocketTranscriptCard';
import { parseWebSocketEvidencePayload } from '../artifacts/websocket/websocket-session-payload';
import { EvidenceCardWithHar } from '../traces/EvidenceCardWithHar';
import { EvidenceCardWithArtifact } from '../traces/EvidenceCardWithArtifact';
import { EvidenceCardWithData } from '../traces/EvidenceCardWithData';
import { RecordingDialogButton } from '../chat/trace-view/components/RecordingDialogButton';
import { BrowserSnapshotScreenshot } from '../chat/trace-view/components/BrowserSnapshotScreenshot';
import { useBrowserScreenshotArtifact } from '../chat/trace-view/hooks/useBrowserScreenshotArtifact';
import { useBrowserScreenshotUrl } from '../chat/trace-view/hooks/useBrowserScreenshotUrl';
import type { PocBlock } from '@terra/shared-schemas-orchestrator';
import type { BaseArtifactDto } from '@terra/shared-contracts-artifact';
import type { InternalEvidenceContentDto } from '@terra/shared-contracts-internal-network';
import { BodyDefault, MetaDefault } from '../../components/typography';
import { Spinner } from '../../components/spinner';
import { useTranslations } from 'next-intl';
import { useInternalEvidenceOptional } from './InternalEvidenceProvider';
import { FindingPocImagePreview } from './FindingPocImagePreview';

type StructuredPocProps = {
  blocks: PocBlock[];
  findingId?: string;
};

function formatPhaseLabel(phase: string): string {
  const formattedPhase = phase.toLowerCase().replaceAll('_', ' ');

  return formattedPhase.charAt(0).toUpperCase() + formattedPhase.slice(1);
}

function PhaseLabel({
  phase,
}: {
  phase: NonNullable<Extract<PocBlock, { type: 'evidence' }>['phase']>;
}) {
  return (
    <MetaDefault color="fg.subtle" whiteSpace="nowrap">
      {formatPhaseLabel(phase)}
    </MetaDefault>
  );
}

function inferArtifactType(
  block: Extract<PocBlock, { type: 'evidence' }>,
): BaseArtifactDto['type'] | undefined {
  if (block.artifactType) {
    return block.artifactType;
  }

  const content = block.manualRequest?.trim();

  if (!content) {
    return undefined;
  }

  const pythonPatterns = [
    /^import\s+\w+/m,
    /^from\s+\w+(\.\w+)*\s+import\s+/m,
    /^def\s+\w+\s*\(/m,
    /^class\s+\w+/m,
    /\bprint\s*\(/,
  ];

  return pythonPatterns.some((pattern) => pattern.test(content))
    ? 'PYTHON'
    : undefined;
}

function createPocArtifact({
  block,
  index,
}: {
  block: Extract<PocBlock, { type: 'evidence' }>;
  index: number;
}): BaseArtifactDto | null {
  const artifactType = inferArtifactType(block);

  if (!artifactType || (!block.manualRequest && !block.manualResponse)) {
    return null;
  }

  const now = new Date(0);

  return {
    id: `poc-artifact-${index}`,
    executionId: null,
    traceId: null,
    threadId: null,
    type: artifactType,
    runtimeId: null,
    toolName: null,
    toolCallId: null,
    actionFileName: null,
    actionContent: block.manualRequest ?? null,
    actionContentSize: block.manualRequest?.length ?? null,
    resultFileName: null,
    resultContent: block.manualResponse ?? null,
    resultContentSize: block.manualResponse?.length ?? null,
    startedAt: now,
    completedAt: null,
    durationMs: null,
    metadata: null,
    createdAt: now,
    updatedAt: now,
  };
}

function createInternalEvidenceArtifact({
  evidenceId,
  evidence,
}: {
  evidenceId: string;
  evidence: InternalEvidenceContentDto;
}): BaseArtifactDto {
  const now = new Date(0);

  return {
    id: evidenceId,
    executionId: null,
    traceId: null,
    threadId: null,
    type: 'CLI',
    runtimeId: null,
    toolName: evidence.command,
    toolCallId: null,
    actionFileName: null,
    actionContent: evidence.actionContent,
    actionContentSize: evidence.actionContent.length,
    resultFileName: null,
    resultContent: evidence.resultContent,
    resultContentSize: evidence.resultContent.length,
    startedAt: now,
    completedAt: null,
    durationMs: null,
    metadata: null,
    createdAt: now,
    updatedAt: now,
  };
}

function InternalEvidencePocCard({
  evidenceId,
  explanation,
  index,
  actions,
  fetchEvidence,
}: {
  evidenceId: string;
  explanation: string;
  index: number;
  actions?: ReactNode;
  fetchEvidence: (evidenceId: string) => Promise<InternalEvidenceContentDto>;
}) {
  const tArtifacts = useTranslations('designSystem.artifacts');
  const [evidence, setEvidence] = useState<InternalEvidenceContentDto | null>(
    null,
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;

    setEvidence(null);
    setFailed(false);
    void fetchEvidence(evidenceId)
      .then((result) => {
        if (active) {
          setEvidence(result);
        }
      })
      .catch((error: unknown) => {
        console.error('Failed to retrieve internal network evidence', error);
        if (active) {
          setFailed(true);
        }
      });

    return () => {
      active = false;
    };
  }, [evidenceId, fetchEvidence]);

  if (failed) {
    return (
      <EvidenceCardWithHar
        evidenceType="artifact"
        explanation={explanation}
        index={index}
        actions={actions}
        escapeExplanationRawHtml
      />
    );
  }

  if (!evidence) {
    return (
      <HStack justify="center" p={4} gap={2}>
        <Spinner size="sm" />
        <BodyDefault color="fg.muted">
          {tArtifacts('loadingEvidence')}
        </BodyDefault>
      </HStack>
    );
  }

  return (
    <EvidenceCardWithArtifact
      artifact={createInternalEvidenceArtifact({ evidenceId, evidence })}
      explanation={explanation}
      index={index}
      actions={actions}
      escapeExplanationRawHtml
    />
  );
}

function EvidencePocBlock({
  block,
  index,
  evidenceIndex,
  internalEvidenceContext,
}: {
  block: Extract<PocBlock, { type: 'evidence' }>;
  index: number;
  evidenceIndex: number;
  internalEvidenceContext: ReturnType<typeof useInternalEvidenceOptional>;
}) {
  const actions = (
    <>
      {block.toolCallId ? (
        <RecordingDialogButton toolCallId={block.toolCallId} />
      ) : null}
      {block.phase ? <PhaseLabel phase={block.phase} /> : null}
    </>
  );

  const screenshotArtifact = useBrowserScreenshotArtifact(
    block.artifactType === 'BROWSER' ? (block.toolCallId ?? '') : '',
  );
  const { screenshotUrl } = useBrowserScreenshotUrl(screenshotArtifact?.id);
  const media = screenshotUrl ? (
    <BrowserSnapshotScreenshot url={screenshotUrl.signedUrl} />
  ) : null;

  const internalEvidenceId =
    block.evidenceRef?.source === 'INTERNAL_EVIDENCE'
      ? block.evidenceRef.id
      : undefined;

  if (internalEvidenceId && internalEvidenceContext) {
    return (
      <InternalEvidencePocCard
        evidenceId={internalEvidenceId}
        explanation={block.explanation}
        index={evidenceIndex}
        actions={actions}
        fetchEvidence={internalEvidenceContext.fetchEvidence}
      />
    );
  }

  if (block.evidenceType === 'email') {
    const emailPayload = parseEmailInboxPayload(block.manualResponse);

    if (emailPayload) {
      return (
        <EvidenceCardWithData
          explanation={block.explanation}
          actions={actions}
          escapeExplanationRawHtml
          index={evidenceIndex}
          evidenceType="email"
        >
          <EmailInboxCard payload={emailPayload} />
        </EvidenceCardWithData>
      );
    }
  }

  if (block.evidenceType === 'oob') {
    const oobPayload = parseOobEvidencePayload(block.manualResponse);

    if (oobPayload) {
      return (
        <EvidenceCardWithData
          explanation={block.explanation}
          actions={actions}
          escapeExplanationRawHtml
          index={evidenceIndex}
          evidenceType="oob"
          detail={<OobEvidenceLabel payload={oobPayload} />}
        >
          <OobInteractionsCard payload={oobPayload} />
        </EvidenceCardWithData>
      );
    }
  }

  if (block.evidenceType === 'websocket') {
    const webSocketPayload = parseWebSocketEvidencePayload(
      block.manualResponse,
    );

    if (webSocketPayload) {
      return (
        <EvidenceCardWithData
          explanation={block.explanation}
          actions={actions}
          escapeExplanationRawHtml
          index={evidenceIndex}
          evidenceType="websocket"
        >
          <WebSocketTranscriptCard payload={webSocketPayload} />
        </EvidenceCardWithData>
      );
    }
  }

  const artifact = createPocArtifact({ block, index });

  if (artifact) {
    return (
      <EvidenceCardWithArtifact
        artifact={artifact}
        explanation={block.explanation}
        index={evidenceIndex}
        actions={actions}
        media={media}
        escapeExplanationRawHtml
      />
    );
  }

  return (
    <EvidenceCardWithHar
      toolExecutionHarId={block.toolExecutionHarId}
      redirectHarIds={block.redirectHarIds}
      evidenceType={block.evidenceType}
      explanation={block.explanation}
      manualRequest={block.manualRequest}
      manualResponse={block.manualResponse}
      index={evidenceIndex}
      actions={actions}
      media={media}
      escapeExplanationRawHtml
    />
  );
}

export function StructuredPoc({ blocks, findingId }: StructuredPocProps) {
  const internalEvidenceContext = useInternalEvidenceOptional();

  if (blocks.length === 0) {
    return null;
  }

  let evidenceCounter = 0;

  return (
    <VStack gap={3} w="full" alignItems="stretch">
      {blocks.map((block, index) => {
        if (block.type === 'narrative') {
          return (
            <Box key={`narrative-${index}`}>
              <MarkdownPreview source={block.content} escapeRawHtml />
            </Box>
          );
        }

        if (block.type === 'image') {
          return (
            <Box key={block.imageId}>
              <VStack
                gap={2}
                alignItems="stretch"
                borderWidth="1px"
                borderColor="border.subtle"
                borderRadius="md"
                p={2}
              >
                <FindingPocImagePreview
                  findingId={findingId}
                  imageId={block.imageId}
                  alt={block.caption ?? block.fileName}
                />
                {block.caption ? (
                  <MetaDefault color="fg.muted">{block.caption}</MetaDefault>
                ) : null}
              </VStack>
            </Box>
          );
        }

        const evidenceIndex = evidenceCounter++;

        return (
          <Box key={`evidence-${index}`}>
            <EvidencePocBlock
              block={block}
              index={index}
              evidenceIndex={evidenceIndex}
              internalEvidenceContext={internalEvidenceContext}
            />
          </Box>
        );
      })}
    </VStack>
  );
}
